// src/oura/delivery/format.ts
//
// How Delivery reads on screen: group dots, the square each item shows, the
// words under a kind. Formatting only; the payload already says everything.

import type { ClientWord, Deliverable, DeliveryGroup, DeliveryItem, ItemKind } from "@service/aeo/delivery";
import { GROUP_TITLE, KIND_LABEL, actorText, shortDay } from "@service/aeo/delivery";
import { myDate } from "@service/aeo/clientDocsRules";
import type { Tone } from "../ui/tokens";

/** "2 Oct" for an instant, on the Malaysian calendar (an ISO's own date is UTC's). */
export const instantDay = (iso: string | null | undefined): string => (iso ? shortDay(myDate(iso)) : "");

/** Who marked an item fixed: the stored name, else whoever ticked the last paste step. */
function markedBy(item: DeliveryItem): string | null {
    if (item.markedFixedBy) return item.markedFixedBy;
    const ticks = (item.pasteList?.steps ?? []).filter((s) => s.ticked && s.tickedBy && s.tickedAt);
    ticks.sort((a, b) => (a.tickedAt! < b.tickedAt! ? -1 : 1));
    return ticks.pop()?.tickedBy ?? null;
}

export { GROUP_TITLE, KIND_LABEL, actorText };

export const GROUP_TONE: Record<DeliveryGroup, Tone> = {
    needs_us: "blue",
    waiting_client: "amber",
    checking: "violet",
    done: "green",
    later: "gray",
};

/** The singular a drawer's status line uses. */
export const GROUP_STATUS: Record<DeliveryGroup, string> = {
    needs_us: "Needs us",
    waiting_client: "Waiting on client",
    checking: "Checking",
    done: "Done",
    later: "Later",
};

export const KIND_TONE: Record<ItemKind, Tone> = { check: "teal", record: "teal", page: "green", confirm: "purple" };

export const CLIENT_WORD_TONE: Record<ClientWord, Tone> = { Done: "green", "In progress": "blue", "Needs you": "amber", "Not started": "gray" };

/** The square an item shows: to do, waiting (Oura's re-check or the client), done, came back. */
export type SquareState = "todo" | "waiting" | "done" | "came_back";

export function squareOf(item: DeliveryItem): SquareState {
    if (item.state === "done") return "done";
    if (item.cameBack) return "came_back";
    if (item.state === "waiting") return "waiting";
    return "todo";
}

/** One short fact beside an item in the checklist or the drawer. */
export function itemFact(item: DeliveryItem): string {
    if (item.doesNotApply) return `Does not apply: ${item.doesNotApply.toLowerCase()}`;
    if (item.closedByHand) return ["Closed by hand", instantDay(item.proof?.at), item.proof?.by ? `by ${item.proof.by}` : ""].filter(Boolean).join(" ");
    if (item.state === "done") {
        if (item.proof?.kind === "note" && item.proof.text) return item.proof.text;
        if (item.kind === "check" && item.pages.length === 0) return "Clear at the start";
        return item.proof?.text ?? "Done";
    }
    if (item.cameBack) {
        const p = item.pages.find((x) => x.cameBack && x.state === "todo");
        return p ? `${p.url} came back` : "Came back";
    }
    if (item.state === "waiting") {
        if (item.waitingOn === "client") return item.askedClientAt ? `Asked ${instantDay(item.askedClientAt)}` : "Waiting on client";
        const notRead = item.pages.filter((p) => p.state === "not_read").length;
        if (item.kind === "page") return `Added ${instantDay(item.markedFixedAt)} · Oura reads it tonight`.replace("Added  ·", "Added ·");
        if (item.markedFixedAt) {
            const by = markedBy(item);
            return `Marked fixed ${instantDay(item.markedFixedAt)}${by ? ` by ${by}` : ""} · Oura re-checks tonight`;
        }
        if (notRead) return `${notRead === 1 ? "1 page" : `${notRead} pages`} not read yet`;
        return "Oura re-checks tonight";
    }
    if (item.kind === "check") {
        const n = item.pages.filter((p) => p.state === "todo").length;
        return n === 1 ? "1 page" : `${n} pages`;
    }
    if (item.kind === "page") {
        const p = item.pages[0];
        if (p) return `${p.url} · ${p.detail ?? "in draft"}`;
        return item.slot?.pageKind ? `${item.slot.pageKind === "new" ? "New page" : "Existing page"}, not started` : "New or existing, agree with the client";
    }
    return item.staffDo ?? "To do";
}

/** The deliverable's month, said once: "Month 1" or "Month 2". */
export function monthTag(d: Pick<Deliverable, "month">): string {
    return `Month ${d.month}`;
}

/** "S2, Bukit Bintang" style chip text for a code with an outlet. */
export function codeChip(d: Pick<Deliverable, "code">): string {
    return d.code;
}

export function plural(n: number, one: string, many = `${one}s`): string {
    return `${n} ${n === 1 ? one : many}`;
}
